import { createRouter, createWebHistory } from "vue-router"
import DashboardLayout from "@/layouts/DashboardLayout.vue"
import { useAuthStore } from "@/stores/auth"
import { useRestaurantStore } from "@/stores/restaurant"
import { ENABLE_ONBOARDING_WIZARD } from "@/config/featureFlags"

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      redirect: "/login",
    },
    {
      path: "/login",
      name: "Login",
      component: () => import("@/views/LoginView.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/onboarding",
      component: () => import("@/layouts/OnboardingLayout.vue"),
      meta: { requiresAuth: true },
      children: [
        {
          path: "",
          name: "Onboarding",
          component: () => import("@/views/dashboard/OnboardingWizardView.vue"),
        },
      ],
    },
    {
      path: "/dashboard",
      component: DashboardLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: "",
          redirect: "/dashboard/menu",
        },
        {
          path: "analytics",
          name: "Analytics",
          component: () => import("@/views/dashboard/AnalyticsView.vue"),
        },
        {
          path: "orders",
          name: "Orders",
          component: () => import("@/views/dashboard/OrdersView.vue"),
          meta: { requiresFullPackage: true },
        },
        {
          path: "active-orders",
          name: "ActiveOrders",
          component: () => import("@/views/dashboard/ActiveOrdersView.vue"),
          meta: { requiresFullPackage: true },
        },
        {
          path: "menu",
          name: "Menu",
          component: () => import("@/views/dashboard/MenuView.vue"),
        },
        {
          path: "menu/:menuId/advanced",
          name: "AdvancedMenu",
          component: () => import("@/views/dashboard/AdvancedMenuView.vue"),
        },
        {
          path: "menu/translations",
          name: "MenuTranslations",
          component: () => import("@/views/dashboard/MenuTranslationsView.vue"),
        },
        {
          path: "products",
          name: "Products",
          component: () => import("@/views/dashboard/ProductsView.vue"),
        },
        {
          path: "tables",
          name: "Tables",
          component: () => import("@/views/dashboard/TablesView.vue"),
        },
        {
          path: "settings",
          redirect: "/dashboard/settings/basic",
        },
        {
          path: "settings/basic",
          name: "SettingsBasic",
          component: () => import("@/views/dashboard/settings/SettingsBasicView.vue"),
        },
        {
          path: "settings/images",
          name: "SettingsImages",
          component: () => import("@/views/dashboard/settings/SettingsImagesView.vue"),
        },
        {
          path: "settings/hours",
          name: "SettingsHours",
          component: () => import("@/views/dashboard/settings/SettingsHoursView.vue"),
        },
        {
          path: "settings/payment",
          name: "SettingsPayment",
          component: () => import("@/views/dashboard/settings/SettingsPaymentView.vue"),
          meta: { requiresFullPackage: true },
        },
        {
          path: "settings/notifications",
          name: "SettingsNotifications",
          component: () => import("@/views/dashboard/settings/SettingsNotificationsView.vue"),
          meta: { requiresFullPackage: true },
        },
        {
          path: "settings/system",
          name: "SettingsSystem",
          component: () => import("@/views/dashboard/settings/SettingsSystemView.vue"),
        },
        {
          path: "restaurant-groups",
          name: "RestaurantGroups",
          component: () => import("@/views/dashboard/RestaurantGroupsView.vue"),
        },
        {
          path: "help",
          name: "Help",
          component: () => import("@/views/dashboard/HelpView.vue"),
        },
        // Admin routes
        {
          path: "admin/orders",
          name: "AdminOrders",
          component: () => import("@/views/dashboard/admin/AdminOrdersView.vue"),
        },
        {
          path: "admin/restaurants",
          name: "AdminRestaurants",
          component: () => import("@/views/dashboard/admin/AdminRestaurantsView.vue"),
        },
        {
          path: "admin/restaurants/new",
          name: "AdminNewRestaurant",
          component: () => import("@/views/dashboard/admin/AdminNewRestaurantView.vue"),
        },
        {
          path: "admin/users",
          name: "AdminUsers",
          component: () => import("@/views/dashboard/admin/AdminUsersView.vue"),
        },
        {
          path: "edit-account",
          name: "EditAccount",
          component: () => import("@/views/dashboard/EditAccountView.vue"),
        },
        {
          path: "edit-account/:id",
          name: "EditUserAccount",
          component: () => import("@/views/dashboard/EditAccountView.vue"),
        },
        {
          path: "admin/restaurant-groups",
          name: "AdminRestaurantGroups",
          component: () => import("@/views/dashboard/admin/AdminRestaurantGroupsView.vue"),
        },
      ],
    },
  ],
})

// Router guard for authentication
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const { apiService } = await import("@/services/api")

  // If route requires auth
  if (to.meta.requiresAuth) {
    // Check if refresh has already failed - if so, don't try to initialize
    // This prevents infinite loops when tokens are missing
    if (apiService.hasRefreshFailed()) {
      // eslint-disable-next-line no-console
      console.warn("⚠️ [Router] Refresh failed, redirecting to login without initialization")
      next({ name: "Login" })
      return
    }

    // Check if user is authenticated
    if (!authStore.isAuthenticated) {
      // Only initialize if not already initializing (App.vue may be doing it)
      if (!authStore.isInitializing) {
        // Try to initialize auth (check if token exists in cookies)
        await authStore.initializeAuth()
      } else {
        // Wait for initialization to complete
        while (authStore.isInitializing) {
          await new Promise(resolve => setTimeout(resolve, 50))
        }
      }
      
      // Check again if authenticated after initialization
      // Also check if refresh failed during initialization
      if (!authStore.isAuthenticated || apiService.hasRefreshFailed()) {
        // Not authenticated or refresh failed, redirect to login
        // eslint-disable-next-line no-console
        console.log("🚪 [Router] Not authenticated or refresh failed, redirecting to login")
        next({ name: "Login" })
        return
      }
    }
  } else if (to.name === "Login" && authStore.isAuthenticated) {
    // If already authenticated and trying to access login, redirect to dashboard
    // Check package type to redirect to appropriate page
    const restaurantStore = useRestaurantStore()
    if (restaurantStore.restaurant?.packageType === "SIMPLE") {
      next({ name: "Menu" })
    } else {
      next({ name: "ActiveOrders" })
    }
    return
  }

  // Check if route requires full package
  if (to.meta.requiresFullPackage) {
    const restaurantStore = useRestaurantStore()
    
    // If restaurant is not loaded yet, wait a bit or redirect
    if (!restaurantStore.restaurant) {
      // Try to wait for restaurant to load (if it's being loaded)
      // Otherwise redirect to menu
      await new Promise(resolve => setTimeout(resolve, 100))
      
      if (!restaurantStore.restaurant) {
        // eslint-disable-next-line no-console
        console.log("🚫 [Router] Restaurant not loaded, redirecting to menu")
        next({ name: "Menu" })
        return
      }
    }
    
    // Check package type
    const packageType = restaurantStore.restaurant?.packageType
    if (packageType === "SIMPLE") {
      // eslint-disable-next-line no-console
      console.log("🚫 [Router] SIMPLE package does not have access to this route, redirecting to menu")
      next({ name: "Menu" })
      return
    }
  }

  // Onboarding: redirect restaurant_owner with incomplete onboarding to wizard (behind feature flag)
  if (to.meta.requiresAuth && authStore.user?.role === "restaurant_owner") {
    const restaurantStore = useRestaurantStore()
    if (!restaurantStore.restaurant) {
      await restaurantStore.initializeUserRestaurant()
    }
    const restaurant = restaurantStore.restaurant
    const onboardingCompleted = restaurant?.onboardingCompleted !== false

    if (ENABLE_ONBOARDING_WIZARD) {
      if (restaurant && !onboardingCompleted && to.path !== "/onboarding") {
        // eslint-disable-next-line no-console
        console.log("📋 [Router] Onboarding not completed, redirecting to onboarding")
        next({ path: "/onboarding" })
        return
      }
    } else if (to.name === "Onboarding") {
      // When wizard is disabled, prevent manual access to /onboarding and send user to dashboard instead
      if (restaurantStore.restaurant?.packageType === "SIMPLE") {
        next({ name: "Menu" })
      } else {
        next({ name: "ActiveOrders" })
      }
      return
    }
  }

  next()
})

export default router
